import { logger } from '../utils/logger';

interface ConversationEntry {
	userPrompt: string;
	componentSummary: string;
	timestamp: number;
}

interface DashboardHistory {
	entries: ConversationEntry[];
	lastAccessedAt: number;
}

/**
 * Build a scope key from userId and dashboardId.
 * Ensures per-user, per-dashboard isolation.
 */
function buildScopeKey(userId: string | undefined, dashboardId: string): string {
	return userId ? `${userId}::${dashboardId}` : dashboardId;
}

/**
 * Manages conversation history scoped per user + dashboard.
 * Each user-dashboard pair has its own isolated history that expires after a configurable TTL.
 */
class DashboardConversationHistory {
	private histories: Map<string, DashboardHistory> = new Map();
	private ttlMs: number = 30 * 60 * 1000; // Default: 30 minutes
	private maxEntries: number = 3;
	private cleanupInterval: NodeJS.Timeout | null = null;

	constructor() {
		this.startCleanup();
	}

	/**
	 * Set the TTL for dashboard histories
	 * @param minutes - TTL in minutes
	 */
	setTTL(minutes: number): void {
		this.ttlMs = minutes * 60 * 1000;
		logger.info(`[DashboardHistory] TTL set to ${minutes} minutes`);
	}

	/**
	 * Set max entries per dashboard
	 */
	setMaxEntries(max: number): void {
		this.maxEntries = max;
	}

	/**
	 * Add a conversation entry for a user's dashboard
	 */
	addEntry(dashboardId: string, userPrompt: string, componentSummary: string, userId?: string): void {
		const key = buildScopeKey(userId, dashboardId);
		let history = this.histories.get(key);
		if (!history) {
			history = { entries: [], lastAccessedAt: Date.now() };
			this.histories.set(key, history);
		}

		history.entries.push({
			userPrompt,
			componentSummary,
			timestamp: Date.now(),
		});

		// Trim to max entries
		if (history.entries.length > this.maxEntries) {
			history.entries = history.entries.slice(-this.maxEntries);
		}

		history.lastAccessedAt = Date.now();
		logger.debug(`[DashboardHistory] Added entry for ${key} (${history.entries.length} total)`);
	}

	/**
	 * Get formatted conversation history for a user's dashboard
	 */
	getHistory(dashboardId: string, userId?: string): string {
		const key = buildScopeKey(userId, dashboardId);
		const history = this.histories.get(key);
		if (!history || history.entries.length === 0) {
			return 'No previous conversation';
		}

		// Check if expired
		if (Date.now() - history.lastAccessedAt > this.ttlMs) {
			this.histories.delete(key);
			logger.debug(`[DashboardHistory] History expired for ${key}`);
			return 'No previous conversation';
		}

		// Update access time
		history.lastAccessedAt = Date.now();

		const lines: string[] = [];
		for (const entry of history.entries) {
			lines.push(`User:\n ${entry.userPrompt}`);
			lines.push(`Assistant:\n ${entry.componentSummary}`);
			lines.push('---');
		}

		return lines.join('\n').trim();
	}

	/**
	 * Clear history for a specific user's dashboard
	 */
	clearDashboard(dashboardId: string, userId?: string): void {
		const key = buildScopeKey(userId, dashboardId);
		this.histories.delete(key);
		logger.debug(`[DashboardHistory] Cleared history for ${key}`);
	}

	/**
	 * Clear all dashboard histories
	 */
	clearAll(): void {
		this.histories.clear();
		logger.info(`[DashboardHistory] All histories cleared`);
	}

	/**
	 * Start periodic cleanup of expired histories
	 */
	private startCleanup(): void {
		this.cleanupInterval = setInterval(() => {
			const now = Date.now();
			let expiredCount = 0;

			for (const [key, history] of this.histories.entries()) {
				if (now - history.lastAccessedAt > this.ttlMs) {
					this.histories.delete(key);
					expiredCount++;
				}
			}

			if (expiredCount > 0) {
				logger.debug(`[DashboardHistory] Cleaned up ${expiredCount} expired histories`);
			}
		}, 5 * 60 * 1000); // Run every 5 minutes
	}

	/**
	 * Stop cleanup interval (for graceful shutdown)
	 */
	destroy(): void {
		if (this.cleanupInterval) {
			clearInterval(this.cleanupInterval);
			this.cleanupInterval = null;
		}
		this.histories.clear();
	}
}

// Export singleton instance
export const dashboardConversationHistory = new DashboardConversationHistory();
